/**
 * accountLedger — single source of truth for the chronological client ledger.
 *
 * Consumed by BOTH the Client Statement PDF (lib/auditExport.ts) and the
 * on-screen Master Audit "Statement" tab (components/admin/FullFinancialAuditTabs.tsx)
 * so the two surfaces are always identical. Framework-free (no React, no jsPDF).
 *
 * Canonical rules (must mirror api/user/my-shares.js + share-calculations.js):
 *  - Shares counted ONLY from purchases with status='active' (incl. COMMISSION_LEVEL_*),
 *    approved conversions, legacy_shares, and net completed transfers. Bonus shares and
 *    commission_transactions.share_commission are NOT added (already in active purchases).
 *  - A commission top-up (is_commission_topup) CREDITS the USDT wallet (+topup_payment_amount);
 *    it adds zero shares.
 *  - Withdrawals are deduped: commission_withdrawals (executed) are authoritative; the
 *    commission_withdrawal_requests are shown for transparency but only move the balance
 *    when there are no executed commission_withdrawals rows.
 *  - Closing balances are sourced from the SHARED canonical calculator
 *    (lib/canonicalBalances.js) so the statement always ties to the exact same
 *    USDT/shares as the dashboard and Master Audit. USDT is anchored to
 *    commission_balances.usdt_balance; any residual (fees / rounding / manual
 *    corrections) is surfaced as one explicit "Reconciliation adjustment" row.
 */

import { computeCanonicalBalances } from './canonicalBalances.js';

export type LedgerKind =
  | 'share_purchase' | 'commission_shares' | 'commission_topup' | 'commission_earned'
  | 'commission_conversion' | 'withdrawal' | 'transfer_sent' | 'transfer_received'
  | 'legacy_shares' | 'reconciliation';

export interface LedgerRow {
  id: string;
  when: string | null;
  kind: LedgerKind;
  label: string;
  status: string;
  terminal: boolean;
  usdt_delta: number;
  shares_delta: number;
  counterparty?: any;
  counterparty_fallback?: any;
  reference?: string | null;
  note?: string;
  usdt_balance: number;
  shares_balance: number;
}

export interface AccountLedger {
  rows: LedgerRow[];
  closingUsdt: number;
  closingShares: number;
  escrowUsdt: number;
  escrowShares: number;
  totalEarnedUsdt: number;
  totalWithdrawn: number;
  reconUsdt: number;
  reconShares: number;
}

const norm = (s: any) => String(s ?? '').trim().toLowerCase();
const num = (v: any) => (typeof v === 'number' ? v : parseFloat(v ?? 0)) || 0;
const round = (n: number, d = 2) => +(Number(n) || 0).toFixed(d);

const T_PURCHASE_ACTIVE = new Set(['active']);
const T_TOPUP = new Set(['approved', 'active', 'completed', 'paid']);
const T_COMMISSION = new Set(['approved', 'paid', 'completed']);
const T_CONVERSION = new Set(['approved']);
const T_WITHDRAWAL = new Set(['completed', 'paid']);
const T_TRANSFER = new Set(['completed']);

export function buildAccountLedger(data: any): AccountLedger {
  const rows: LedgerRow[] = [];
  const sp = data?.share_purchases || {};
  const ce = data?.commissions_earned || {};
  const cc = data?.commission_conversions || {};
  const w = data?.withdrawals || {};
  const st = data?.share_transfers || {};
  const bal = data?.commission_balance || {};
  const signup = data?.profile?.created_at || null;

  // Legacy opening shares
  const legacyShares = num(bal.legacy_shares);
  if (legacyShares !== 0) {
    rows.push({ id: 'legacy', when: signup, kind: 'legacy_shares', label: 'Legacy shares (opening balance)', status: 'active', terminal: true, usdt_delta: 0, shares_delta: legacyShares, usdt_balance: 0, shares_balance: 0 });
  }

  // Purchases (real, commission-level, and top-ups)
  for (const p of sp.all || []) {
    const status = norm(p.status);
    if (p.is_commission_topup) {
      const terminal = T_TOPUP.has(status);
      rows.push({ id: `sp-${p.id}`, when: p.created_at, kind: 'commission_topup', label: 'Commission wallet top-up (USDT received)', status: p.status || '', terminal, usdt_delta: terminal ? num(p.topup_payment_amount || p.total_amount) : 0, shares_delta: 0, reference: p.transaction_reference || null, usdt_balance: 0, shares_balance: 0 });
      continue;
    }
    const isCommLevel = String(p.payment_method || '').startsWith('COMMISSION_LEVEL_');
    const terminal = T_PURCHASE_ACTIVE.has(status);
    rows.push({ id: `sp-${p.id}`, when: p.created_at, kind: isCommLevel ? 'commission_shares' : 'share_purchase', label: isCommLevel ? `Commission shares credited (${String(p.payment_method).replace('COMMISSION_LEVEL_', 'level ')})` : `Share purchase — ${p.package_name || 'package'}`, status: p.status || '', terminal, usdt_delta: 0, shares_delta: terminal ? num(p.shares_purchased) : 0, reference: p.transaction_reference || null, note: p.bonus_shares ? `Bonus shares (not counted in total): ${round(num(p.bonus_shares), 4)}` : undefined, usdt_balance: 0, shares_balance: 0 });
  }

  // USDT commission earned (shares already represented by COMMISSION_LEVEL_* purchases)
  for (const c of ce.commission_transactions || []) {
    const terminal = T_COMMISSION.has(norm(c.status));
    rows.push({ id: `ct-${c.id}`, when: c.created_at, kind: 'commission_earned', label: 'Commission earned (direct)', status: c.status || '', terminal, usdt_delta: terminal ? num(c.usdt_commission) : 0, shares_delta: 0, counterparty: c.counterparty_user, counterparty_fallback: c.referred_id, reference: c.share_purchase_id ? `purchase #${c.share_purchase_id}` : null, usdt_balance: 0, shares_balance: 0 });
  }
  for (const c of ce.multi_level_commissions || []) {
    const terminal = T_COMMISSION.has(norm(c.status));
    rows.push({ id: `mlc-${c.id}`, when: c.created_at, kind: 'commission_earned', label: `Commission earned (level ${c.level})`, status: c.status || '', terminal, usdt_delta: terminal ? num(c.usdt_commission) : 0, shares_delta: 0, counterparty: c.counterparty_user, counterparty_fallback: c.referred_id, reference: c.original_purchase_id ? `purchase #${c.original_purchase_id}` : null, usdt_balance: 0, shares_balance: 0 });
  }

  // Conversions (USDT spent → shares)
  for (const r of cc.rows || []) {
    const terminal = T_CONVERSION.has(norm(r.status));
    rows.push({ id: `cc-${r.id}`, when: r.approved_at || r.created_at, kind: 'commission_conversion', label: 'Commission converted to shares', status: r.status || '', terminal, usdt_delta: terminal ? -num(r.usdt_amount) : 0, shares_delta: terminal ? num(r.shares_requested) : 0, reference: r.phase_number != null ? `phase ${r.phase_number}` : null, usdt_balance: 0, shares_balance: 0 });
  }

  // Withdrawals (deduped: executed rows authoritative)
  const cws = w.commission_withdrawals || [];
  const cwReqs = w.commission_withdrawal_requests || [];
  const reqMovesBalance = cws.length === 0;
  for (const r of cwReqs) {
    const terminal = reqMovesBalance && T_WITHDRAWAL.has(norm(r.status));
    rows.push({ id: `cwr-${r.id}`, when: r.completed_at || r.approved_at || r.created_at, kind: 'withdrawal', label: `Commission withdrawal request${r.currency ? ` (${r.currency})` : ''}`, status: r.status || '', terminal, usdt_delta: terminal ? -num(r.withdrawal_amount) : 0, shares_delta: 0, reference: r.transaction_hash || null, usdt_balance: 0, shares_balance: 0 });
  }
  for (const r of cws) {
    const terminal = T_WITHDRAWAL.has(norm(r.status));
    rows.push({ id: `cw-${r.id}`, when: r.completed_at || r.approved_at || r.created_at, kind: 'withdrawal', label: `Commission withdrawal (${r.withdrawal_type || 'usdt'})`, status: r.status || '', terminal, usdt_delta: terminal ? -num(r.net_amount || r.amount) : 0, shares_delta: 0, reference: r.transaction_hash || null, usdt_balance: 0, shares_balance: 0 });
  }

  // Transfers (shares only)
  for (const t of st.sent || []) {
    const terminal = T_TRANSFER.has(norm(t.status));
    rows.push({ id: `tx-s-${t.id}`, when: t.completed_at || t.created_at, kind: 'transfer_sent', label: 'Share transfer sent', status: t.status || '', terminal, usdt_delta: 0, shares_delta: terminal ? -num(t.shares_transferred) : 0, counterparty: t.counterparty_user, counterparty_fallback: t.recipient_user_id, note: t.transfer_reason || undefined, usdt_balance: 0, shares_balance: 0 });
  }
  for (const t of st.received || []) {
    const terminal = T_TRANSFER.has(norm(t.status));
    rows.push({ id: `tx-r-${t.id}`, when: t.completed_at || t.created_at, kind: 'transfer_received', label: 'Share transfer received', status: t.status || '', terminal, usdt_delta: 0, shares_delta: terminal ? num(t.shares_transferred) : 0, counterparty: t.counterparty_user, counterparty_fallback: t.sender_user_id, note: t.transfer_reason || undefined, usdt_balance: 0, shares_balance: 0 });
  }

  return finalize(rows, bal, computeCanonicalBalances(toCanonicalRawData(data)));
}

// Map the statement's nested `data` payload to the flat CanonicalRawData shape.
function toCanonicalRawData(data: any) {
  const sp = data?.share_purchases || {};
  const ce = data?.commissions_earned || {};
  const cc = data?.commission_conversions || {};
  const w = data?.withdrawals || {};
  const st = data?.share_transfers || {};
  return {
    purchases: sp.all || [],
    commissionTransactions: ce.commission_transactions || [],
    multiLevelCommissions: ce.multi_level_commissions || [],
    conversions: cc.rows || [],
    withdrawalsExecuted: w.commission_withdrawals || [],
    withdrawalRequests: w.commission_withdrawal_requests || [],
    transfersSent: st.sent || [],
    transfersReceived: st.received || [],
    commissionBalance: data?.commission_balance || null,
  };
}

function finalize(rows: LedgerRow[], bal: any, canonical: any): AccountLedger {
  rows.sort((a, b) => {
    const ta = a.when ? new Date(a.when).getTime() : 0;
    const tb = b.when ? new Date(b.when).getTime() : 0;
    if (ta !== tb) return ta - tb;
    return a.id.localeCompare(b.id);
  });

  let usdt = 0, shares = 0;
  for (const r of rows) {
    if (r.terminal) { usdt += r.usdt_delta; shares += r.shares_delta; }
    r.usdt_balance = round(usdt, 2);
    r.shares_balance = round(shares, 8);
  }

  // Closing figures come from the SHARED canonical calculator so every surface
  // agrees. reconUsdt is canonical.residual (fees / rounding / manual corrections
  // not reconstructable from the rows); canonical shares equal the row walk.
  const closingUsdt = round(num(canonical?.wallet?.usdt), 2);
  const closingShares = round(num(canonical?.equityShares), 8);
  const reconUsdt = round(num(canonical?.residual), 2);
  const reconShares = 0;
  const lastWhen = rows.length ? rows[rows.length - 1].when : null;

  if (Math.abs(reconUsdt) >= 0.01) {
    usdt = closingUsdt;
    rows.push({ id: 'recon', when: lastWhen, kind: 'reconciliation', label: 'Reconciliation adjustment (fees / rounding / manual corrections)', status: 'adjustment', terminal: true, usdt_delta: reconUsdt, shares_delta: 0, usdt_balance: round(usdt, 2), shares_balance: round(shares, 8) });
  }

  return {
    rows,
    closingUsdt,
    closingShares,
    escrowUsdt: round(num(bal.escrowed_amount), 2),
    escrowShares: round(num(bal.escrowed_shares), 8),
    totalEarnedUsdt: round(num(bal.total_earned_usdt), 2),
    totalWithdrawn: round(num(bal.total_withdrawn), 2),
    reconUsdt,
    reconShares,
  };
}
